// Auth Service - Phase 8: Identifier login, required email signup, forgot password
import api from "./api";

const ADMIN_ROLES = new Set(["admin", "moderator", "support", "superadmin"]);

function clearAdminSession() {
    localStorage.removeItem("adminSessionId");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("adminId");
    try {
        window.dispatchEvent(new CustomEvent("admin-auth-cleared"));
    } catch {
        /* noop */
    }
}

function clearClientSession() {
    localStorage.removeItem("sessionId");
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("clubsClientToken");
    localStorage.removeItem("clubsClientUser");
    localStorage.removeItem("client_session_id");
    localStorage.removeItem("client_token");
}

function clearVendorSession() {
    localStorage.removeItem("vendor_token");
    localStorage.removeItem("vendorToken");
}

export const login = async (identifier, password) => {
    const axiosResponse = await api.post("/auth/login", {
        identifier,
        password,
    });
    const response = axiosResponse.data || axiosResponse;
    if (response.session_id) {
        const role = (response.user?.role || "").toLowerCase();
        if (ADMIN_ROLES.has(role)) {
            clearClientSession();
            clearVendorSession();
            localStorage.setItem("adminSessionId", response.session_id);
            localStorage.setItem("adminUser", JSON.stringify(response.user));
            if (response.user?.id) {
                localStorage.setItem("adminId", response.user.id);
            }
            window.dispatchEvent(
                new CustomEvent("admin-auth-changed", {
                    detail: { user: response.user, sessionId: response.session_id },
                }),
            );
        } else {
            clearAdminSession();
            clearVendorSession();
            localStorage.setItem("sessionId", response.session_id);
            localStorage.setItem("user", JSON.stringify(response.user));
        }
    }
    return response;
};

export const signup = async ({
    email,
    password,
    username,
    phone_e164,
    referralCode,
}) => {
    const axiosResponse = await api.post("/auth/signup", {
        email,
        password,
        username: username || undefined,
        phone_e164: phone_e164 || undefined,
        referral_code:
            referralCode || localStorage.getItem("pendingReferralCode"),
    });
    const response = axiosResponse.data || axiosResponse;

    localStorage.removeItem("pendingReferralCode");
    localStorage.removeItem("pendingReferralToken");

    if (response.session_id) {
        clearAdminSession();
        clearVendorSession();
        localStorage.removeItem("clubsClientToken");
        localStorage.removeItem("clubsClientUser");
        localStorage.setItem("sessionId", response.session_id);
        if (response.token) {
            localStorage.setItem("authToken", response.token);
        }
        localStorage.setItem("user", JSON.stringify(response.user));
        // One-time "+5 XP — welcome to the leaderboard" toast trigger.
        // AppContext picks this flag up after the user lands on the home
        // shell and clears it after the toast fires once.
        try {
            localStorage.setItem("welcome_xp_toast_pending", "1");
        } catch {
            /* private mode / quota — non-fatal */
        }
    }
    return response;
};

export const logout = async () => {
    try {
        await api.post("/auth/logout", {});
    } catch (e) {
        // Ignore errors
    }
    localStorage.removeItem("sessionId");
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("clubsClientToken");
    localStorage.removeItem("clubsClientUser");
    localStorage.removeItem("client_session_id");
    localStorage.removeItem("client_token");
};

export const getCurrentUser = async () => {
    const axiosResponse = await api.get("/auth/me");
    return axiosResponse.data || axiosResponse;
};

export const changePassword = async (oldPassword, newPassword) => {
    const axiosResponse = await api.post("/auth/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
    });
    return axiosResponse.data || axiosResponse;
};

// Forgot password flow
export const forgotPasswordStart = async (identifier) => {
    const axiosResponse = await api.post("/auth/forgot-password/start", {
        identifier,
    });
    return axiosResponse.data || axiosResponse;
};

export const forgotPasswordVerify = async (identifier, code, newPassword) => {
    const axiosResponse = await api.post("/auth/forgot-password/verify", {
        identifier,
        code,
        new_password: newPassword,
    });
    return axiosResponse.data || axiosResponse;
};

export const isAuthenticated = () => {
    return !!localStorage.getItem("sessionId");
};

export const getStoredUser = () => {
    try {
        return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
        return null;
    }
};

// Refresh current user from server and update localStorage
export const refreshUser = async () => {
    try {
        const user = await getCurrentUser();
        const userData = user.user || user;
        if (userData) {
            localStorage.setItem("user", JSON.stringify(userData));
        }
        return userData;
    } catch {
        return null;
    }
};

export default {
    login,
    signup,
    logout,
    getCurrentUser,
    changePassword,
    forgotPasswordStart,
    forgotPasswordVerify,
    isAuthenticated,
    getStoredUser,
    refreshUser,
};
